import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2 } from "lucide-react";

const TEMPLATE_HEADER = "sku,name,supplier_name,aliases,category,unit,supplier_code,location_code,container_count,qty_per_container,description,unit_cost";
const TEMPLATE_EXAMPLE = [
  TEMPLATE_HEADER,
  "MAHUA,Mahua Oil,,,essential_oil,kg,ABLE,PH-PARK,1,20,20 kg can,",
  "NS-LAV,Lavender Super,,,fragrance,kg,NISHA,PH-PARK,1,26.600,30 kg can (part used),",
  "NS-LAV,Lavender Super,,,fragrance,kg,NISHA,PH-B702-AR1-A1,1,5.500,5 kg can,",
].join("\n");

const CATEGORIES = ["essential_oil", "aroma_chemical", "fragrance", "butter", "carrier_oil", "packaging", "finished_good"];
const UNITS = ["kg", "L", "pcs"];

/** Minimal CSV parser that understands double-quoted cells. */
function parseCSV(text) {
  const rows = [];
  let row = [], cell = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') inQ = false;
      else cell += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some(v => v.trim() !== "")) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some(v => v.trim() !== "")) rows.push(row);
  return rows;
}

function rowsFromCSV(text) {
  const raw = parseCSV(text.trim());
  if (!raw.length) return { rows: [], errors: ["No data"] };
  const header = raw[0].map(h => h.trim().toLowerCase());
  const idx = (name) => header.indexOf(name);
  const required = ["sku", "name", "location_code", "qty_per_container"];
  const missing = required.filter(r => idx(r) === -1);
  if (missing.length) return { rows: [], errors: [`Missing columns: ${missing.join(", ")}`] };

  const rows = [];
  const errors = [];
  raw.slice(1).forEach((r, i) => {
    const get = (name) => (idx(name) === -1 ? "" : (r[idx(name)] || "").trim());
    const n = i + 2; // human line number
    const qty = Number(get("qty_per_container"));
    const count = Number(get("container_count") || "1");
    const category = get("category") || "fragrance";
    const unit = get("unit") || "kg";
    if (!get("sku") || !get("name")) { errors.push(`Line ${n}: sku and name required`); return; }
    if (!qty || qty <= 0) { errors.push(`Line ${n}: qty_per_container must be > 0`); return; }
    if (!count || count < 1) { errors.push(`Line ${n}: container_count must be ≥ 1`); return; }
    if (!CATEGORIES.includes(category)) { errors.push(`Line ${n}: category must be one of ${CATEGORIES.join("/")}`); return; }
    if (!UNITS.includes(unit)) { errors.push(`Line ${n}: unit must be kg, L or pcs`); return; }
    rows.push({
      sku: get("sku"), name: get("name"),
      supplier_name: get("supplier_name") || null,
      aliases: get("aliases") ? get("aliases").split("|").map(s => s.trim()).filter(Boolean) : [],
      category, base_unit: unit,
      supplier_code: get("supplier_code") || null,
      location_code: get("location_code"),
      container_count: count,
      qty_per_container: qty,
      description: get("description") || "container",
      unit_cost: get("unit_cost") ? Number(get("unit_cost")) : null,
    });
  });
  return { rows, errors };
}

export default function ImportStock() {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const preview = () => {
    setResult(null);
    setParsed(rowsFromCSV(text));
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/opening-stock", { rows: parsed.rows });
      setResult(data);
      setParsed(null); setText("");
      toast.success(`Imported ${data.rows} rows`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_EXAMPLE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "opening-stock-template.csv";
    a.click();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="import-stock">
      <div>
        <div className="data-label">One-time setup</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Import Existing Stock</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Fill the Excel/CSV template can by can, paste or upload it here, check the preview, import.
          Missing items are created automatically. Rows with the same SKU + code become one batch,
          each can at its own location (so a part-used can in the parking and a 5 kg can in 702 are two rows).
        </p>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button className="btn-ghost text-sm" onClick={downloadTemplate} data-testid="import-template">
            <Download className="w-4 h-4 mr-1" /> Download template
          </button>
          <label className="btn-ghost text-sm cursor-pointer">
            <Upload className="w-4 h-4 mr-1" /> Upload CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file" />
          </label>
          <span className="text-xs text-muted-foreground">
            Columns: sku, name, supplier_name, aliases (separate with |), category, unit,
            supplier_code (blank = OPN), location_code, container_count, qty_per_container, description, unit_cost
          </span>
        </div>
        <textarea
          className="field min-h-[160px] font-mono text-xs"
          placeholder={TEMPLATE_EXAMPLE}
          value={text}
          onChange={(e) => { setText(e.target.value); setParsed(null); }}
          data-testid="import-text"
        />
        <div className="flex justify-end">
          <button className="btn-primary" onClick={preview} disabled={!text.trim()} data-testid="import-preview-btn">
            Preview
          </button>
        </div>
      </div>

      {parsed && (
        <div className="bg-white border border-border rounded-sm" data-testid="import-preview">
          {parsed.errors.length > 0 && (
            <div className="p-4 border-b border-border">
              <div className="data-label text-destructive mb-1">Fix these first ({parsed.errors.length})</div>
              <ul className="text-xs text-destructive space-y-0.5">
                {parsed.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          <div className="p-4">
            <div className="data-label mb-2">{parsed.rows.length} rows ready</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground uppercase tracking-wider">
                    <th className="py-1 pr-3">SKU</th><th className="pr-3">Name</th>
                    <th className="pr-3">Other name</th><th className="pr-3">Category</th>
                    <th className="pr-3">Code</th>
                    <th className="pr-3">Location</th><th className="pr-3 text-right">Containers</th>
                    <th className="pr-3 text-right">Total qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {parsed.rows.map((r, i) => (
                    <tr key={i}>
                      <td className="py-1.5 pr-3 font-mono">{r.sku}</td>
                      <td className="pr-3 font-medium">{r.name}</td>
                      <td className="pr-3 text-muted-foreground">{r.supplier_name || "—"}</td>
                      <td className="pr-3">{r.category}</td>
                      <td className="pr-3 font-mono">{r.supplier_code || "OPN"}</td>
                      <td className="pr-3 font-mono">{r.location_code}</td>
                      <td className="pr-3 text-right font-mono">{r.container_count} × {r.qty_per_container}</td>
                      <td className="pr-3 text-right font-mono font-semibold">
                        {(r.container_count * r.qty_per_container).toLocaleString()} {r.base_unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="p-4 border-t border-border flex justify-end">
            <button className="btn-primary" onClick={doImport}
                    disabled={busy || parsed.errors.length > 0 || parsed.rows.length === 0}
                    data-testid="import-submit">
              {busy ? "Importing…" : `Import ${parsed.rows.length} rows`}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-success/10 border border-success/30 rounded-sm p-4 flex items-center gap-3"
             data-testid="import-result">
          <CheckCircle2 className="w-5 h-5 text-success" />
          <div className="text-sm">
            Imported <b>{result.rows}</b> rows — {result.created_items} new items,{" "}
            {result.batches} batches, {result.containers} containers. Check Find Stock to verify.
          </div>
        </div>
      )}
    </div>
  );
}
